import { Divider } from '@mui/material';
import type { UsageProductInput } from '@weft/types';
import { BILLING_INTERVALS, DEFAULT_CURRENCY } from '@weft/types';
import { isEmptyArray, isNotEmptyArray, withDefault } from '@weft/utils';
import { getSplitVolumeUsageProducts } from 'apps/app/src/utils/get-split-usage-products';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import type { PlanInputForm } from '../CreatePlanForm/create-plan-form-types/plan-input-form';
import { EmptyLivePreview } from './create-plan-live-preview-components/EmptyLivePreview';
import { FixedProductsSection } from './create-plan-live-preview-components/FixedProductsSection';
import { LivePreviewContainer } from './create-plan-live-preview-components/LivePreviewContainer';
import { PromotionsSection } from './create-plan-live-preview-components/PromotionsSection';
import { UsageProductsSection } from './create-plan-live-preview-components/UsageProductsSection';
import { usePlanTotalAmount } from './create-plan-live-preview-hooks/use-plan-total-amount';

type CreatePlanLivePreviewProps = {
  title: React.ReactNode;
  onHide?: () => void;
};

// TODO: change name to CreateLivePreview or something else since this component is being use both in CreatePlanPage and CreateInvoicePage
export const CreatePlanLivePreview: React.FC<CreatePlanLivePreviewProps> = ({ title, onHide }) => {
  const totalAmount = usePlanTotalAmount();
  const { watch } = useFormContext<PlanInputForm>();

  const billingInterval = withDefault(watch('billingData.billingInterval'), BILLING_INTERVALS.Monthly);
  const fixedProducts = withDefault(watch('fixedProducts'), []);
  const usageProducts = withDefault(watch('usageProducts'), []);
  const products = [...fixedProducts, ...usageProducts];
  const promotions = withDefault(watch('promotions'), []);

  const itemsCount = products.length + promotions.length;

  const { nonVolumeBasedProducts, volumeBasedProducts } = getSplitVolumeUsageProducts(usageProducts as UsageProductInput[]);

  if (isEmptyArray(products) && isEmptyArray(promotions)) {
    return (
      <EmptyLivePreview
        title={title}
        currency={DEFAULT_CURRENCY}
        itemsCount={itemsCount}
        totalAmount={totalAmount}
        onHide={onHide}
      />
    );
  }

  return (
    <LivePreviewContainer
      title={title}
      currency={DEFAULT_CURRENCY}
      itemsCount={itemsCount}
      totalAmount={totalAmount}
      onHide={onHide}
    >
      <div className="flex flex-col py-4">
        {isNotEmptyArray(fixedProducts) && (
          <FixedProductsSection
            fixedProducts={fixedProducts}
            billingInterval={billingInterval}
            currency={DEFAULT_CURRENCY}
          />
        )}

        {isNotEmptyArray(fixedProducts) && isNotEmptyArray(usageProducts) && (
          <Divider className="my-6"/>
        )}

        {isNotEmptyArray(nonVolumeBasedProducts) && (
          <UsageProductsSection
            usageProducts={nonVolumeBasedProducts}
            billingInterval={billingInterval}
            currency={DEFAULT_CURRENCY}
          />
        )}

        {isNotEmptyArray(volumeBasedProducts) && (
          <UsageProductsSection
            usageProducts={volumeBasedProducts}
            title='Usage-based Fees - Volume pricing'
            billingInterval={billingInterval}
            currency={DEFAULT_CURRENCY}
          />
        )}

        {(isNotEmptyArray(fixedProducts) || isNotEmptyArray(usageProducts)) && isNotEmptyArray(promotions) && (
          <Divider className="my-6"/>
        )}

        {isNotEmptyArray(promotions) && (
          <PromotionsSection
            promotions={promotions}
            currency={DEFAULT_CURRENCY}
          />
        )}
      </div>
    </LivePreviewContainer>
  );
};
